import {
  type FlowDefinitionTemplateListResponse as FlowApiFlowDefinitionTemplateListResponse,
  type FlowDefinitionState,
  type FlowDefinitionTemplateMetadata,
  FlowDefinitionTemplateVisibility,
  type FlowTagCategory,
} from 'flow-schema';
import { z } from 'zod';

import { type FlowData } from './flowDefinition.js';
import { FlowTagFilterSchema, type FlowTagRecord } from './tag.js';
import { isDefined } from '../../utils.js';
import { type BaseItem } from '../common.js';

export interface TemplateTag {
  name: string;
  category: FlowTagCategory;
}

interface WithThumbnailUrl {
  thumbnailUrl?: string;
}

export const CaseStudySchema = z.object({
  hyperlinkUrl: z.string(),
  imageUrl: z.string(),
  title: z.string(),
});
export type CaseStudy = z.infer<typeof CaseStudySchema>;

export const DemoVideoSchema = z.object({
  videoUrl: z.string(),
});
export type DemoVideo = z.infer<typeof DemoVideoSchema>;

interface BaseFlowDefinitionTemplate {
  name: string;
  organizationId: string;
  /** Required: clones this flow definition (with subflows) to create the template. */
  sourceFlowDefinitionId: string;
  content: string;
  detailedContentMarkdown: string;
  createdById?: string;
  updatedById?: string;
  metadata?: FlowDefinitionTemplateMetadata;
  tags?: FlowTagRecord[];
  visibility: FlowDefinitionTemplateVisibility;
}

export interface FlowDefinitionTemplate
  extends BaseFlowDefinitionTemplate, BaseItem, WithThumbnailUrl {
  flowDefinitionId: string | undefined;
  isBookmarked?: boolean;
  createdByEmail?: string;
  version?: number;
  state?: FlowDefinitionState;
  data?: FlowData;
}

/**
 * The list response flow-api publishes, with the items kept as this package's template model.
 *
 * The envelope comes from flow-schema so its paging fields cannot be re-copied by hand here and
 * drift; the items stay local because this model is what the apps are typed against.
 */
export interface FlowDefinitionTemplateListResponse extends Omit<
  FlowApiFlowDefinitionTemplateListResponse,
  'flowDefinitionTemplates'
> {
  flowDefinitionTemplates: FlowDefinitionTemplate[];
}

type WithOptionalKeys<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

type FlowDefinitionUpdateValues = Omit<BaseFlowDefinitionTemplate, 'tags'> & {
  tags?: TemplateTag[];
};

export type CreateValues = WithOptionalKeys<FlowDefinitionUpdateValues, 'organizationId'>;
export type UpdateValues = Partial<Omit<FlowDefinitionUpdateValues, 'sourceFlowDefinitionId'>>;

/**
 * The requesting user is taken from the authenticated request, so there is no parameter for it:
 * flow-api rejects any key outside this set.
 */
export const findAllOptionsSchema = z.object({
  organizationId: z.array(z.uuid()).optional(),
  nameSearch: z.string().optional(),
  tags: FlowTagFilterSchema.optional().transform((val) =>
    isDefined(val) ? JSON.stringify(val) : undefined
  ),

  limit: z.coerce.number().optional(),
  offset: z.coerce.number().optional(),
  orderBy: z.enum(['name', 'created', 'updated']).optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
  visibility: z.array(z.enum(FlowDefinitionTemplateVisibility)).optional(),
});
export type FindAllOptions = z.input<typeof findAllOptionsSchema>;

export const thumbnailUploadSchema = z.object({
  thumbnailData: z.string().base64(),
  contentType: z.string(),
});
export type ThumbnailUploadRequest = z.infer<typeof thumbnailUploadSchema>;

export const thumbnailUploadResponseSchema = z.object({
  thumbnailUrl: z.string().url(),
});
export type ThumbnailUploadResponse = z.infer<typeof thumbnailUploadResponseSchema>;

export const caseStudyImageUploadSchema = z.object({
  caseStudyImageData: z.string().base64(),
  contentType: z.string(),
});
export type CaseStudyImageUploadRequest = z.infer<typeof caseStudyImageUploadSchema>;

export const caseStudyImageUploadResponseSchema = z.object({
  caseStudyImageUrl: z.string().url(),
});
export type CaseStudyImageUploadResponse = z.infer<typeof caseStudyImageUploadResponseSchema>;
